import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from '../../utils/axios';
import {
  Eye, EyeOff, Mail, Lock, User, MapPin, Globe, Building,
  CheckCircle, AlertCircle, ChevronLeft, ChevronRight,
  UserPlus, LogIn, UserCog, ClipboardCheck, BookOpen as BookOpenIcon, HelpCircle, Loader
} from 'lucide-react';

const STORAGE_KEY = 'asfirj_signup_form';
const disciplineOptions = [
  'Agricultural sciences', 'Allied Health Sciences', 'Anthropology', 'Arts', 'Biology',
  'Chemistry', 'Climate Change', 'Computer and Information Sciences', 'Earth Sciences',
  'Engineering and Technology', 'Environmental Sciences', 'Epidemiology', 'Humanities',
  'Leadership and Management', 'Life Sciences', 'Materials Science', 'Mathematics',
  'Medicine', 'Physics and Astronomy', 'Politics', 'Public & Global Health',
  'Social and Behavioral Sciences', 'Statistics', 'Theology', 'Other'
];

const AuthorSignup = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(() => {
    const savedData = localStorage.getItem(STORAGE_KEY);
    if (savedData) {
      try { return JSON.parse(savedData); } catch (e) { console.error(e); }
    }
    return {
      prefix: '', firstName: '', lastName: '', otherName: '', email: '',
      orcid: '', affiliation: '', affiliationCountry: '', affiliationCity: '',
      discipline: '', otherDiscipline: '', reviewAvailability: 'no',
      password: '', confirmPassword: '', recaptchaToken: '' // Added token here
    };
  });

  const [errors, setErrors] = useState({});
  const [recaptchaVerified, setRecaptchaVerified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [currentStep, setCurrentStep] = useState(() => {
    const savedStep = localStorage.getItem(`${STORAGE_KEY}_step`);
    return savedStep ? parseInt(savedStep) : 1;
  });
  const [showOtherDiscipline, setShowOtherDiscipline] = useState(false);

  const recaptchaWidgetId = useRef(null);

  // --- RECAPTCHA LOGIC START ---
  
  const handleRecaptcha = (response) => {
    setRecaptchaVerified(!!response);
    setFormData(prev => ({ ...prev, recaptchaToken: response }));
  };

  const handleRecaptchaExpired = () => {
    setRecaptchaVerified(false);
    setFormData(prev => ({ ...prev, recaptchaToken: '' }));
    setMessage({ type: 'error', text: 'reCAPTCHA expired. Please verify again.' });
  };

  const resetRecaptcha = () => {
    if (window.grecaptcha && recaptchaWidgetId.current !== null) {
      try {
        window.grecaptcha.reset(recaptchaWidgetId.current);
      } catch (error) {
        console.error('Error resetting reCAPTCHA:', error);
      }
    }
    setRecaptchaVerified(false);
  };

  useEffect(() => {
    const loadRecaptcha = () => {
      // We only render if we are on Step 3 and the container exists
      if (currentStep === 3 && window.grecaptcha && document.getElementById('recaptcha-container')) {
        if (recaptchaWidgetId.current === null) {
          try {
            recaptchaWidgetId.current = window.grecaptcha.render('recaptcha-container', {
              sitekey: '6LcEcsUrAAAAACd3CAtZIO54BjvF7viwD__b0vTB',
              callback: handleRecaptcha,
              'expired-callback': handleRecaptchaExpired
            });
          } catch (error) {
            console.error('reCAPTCHA render error:', error);
          }
        }
      }
    };

    if (currentStep === 3) {
      if (window.grecaptcha && window.grecaptcha.render) {
        // Small delay to ensure React has painted the Step 3 DOM
        setTimeout(loadRecaptcha, 100);
      } else {
        window.onRecaptchaLoad = () => loadRecaptcha();
        const existingScript = document.querySelector('script[src*="recaptcha"]');
        if (!existingScript) {
          const script = document.createElement('script');
          script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit';
          script.async = true;
          script.defer = true;
          document.head.appendChild(script);
        }
      }
    }

    return () => {
      // We don't nullify widgetId on every effect run, 
      // but we should if the component unmounts
    };
  }, [currentStep]);

  // --- RECAPTCHA LOGIC END ---

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    localStorage.setItem(`${STORAGE_KEY}_step`, currentStep.toString());
  }, [currentStep]);

  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleRadioChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.prefix) newErrors.prefix = 'Prefix is required';
    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.affiliation) newErrors.affiliation = 'Affiliation is required';
    if (!formData.affiliationCountry) newErrors.affiliationCountry = 'Country is required';
    if (!formData.affiliationCity) newErrors.affiliationCity = 'City is required';
    if (!formData.discipline) newErrors.discipline = 'Primary discipline is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Include uppercase, lowercase, and a number';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    let isValid = currentStep === 1 ? validateStep1() : validateStep2();
    if (isValid) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep(prev => prev - 1);
    // Important: Reset reCAPTCHA reference when leaving step 3
    recaptchaWidgetId.current = null;
    setRecaptchaVerified(false);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep3()) return;
    if (!recaptchaVerified && process.env.NODE_ENV !== 'development') {
      setMessage({ type: 'error', text: 'Please complete reCAPTCHA' });
      return;
    }

    setIsSubmitting(true);
    const submitData = {
      ...formData,
      discipline: formData.discipline === 'Other' ? formData.otherDiscipline : formData.discipline,
      recaptchaToken: formData.recaptchaToken || 'development-skip'
    };

    try {
      const response = await axios.post('/api/authors/signup', submitData);
      if (response.data.status === 'success') {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(`${STORAGE_KEY}_step`);
        setMessage({ type: 'success', text: 'Registration successful! Redirecting...' });
        setTimeout(() => navigate('/portal/verify-notice', { state: { email: formData.email } }), 2000);
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Registration failed' });
      resetRecaptcha();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-[#f6ecf6] min-h-screen flex flex-col">
      <div className="flex-1 flex">
        {/* Sidebar remains the same as your provided code */}
        <div className="sidebar w-64 hidden lg:block" style={{ background: 'linear-gradient(to bottom, #250242, #550f4f)' }}>
           <div className="p-6">
            <h2 className="text-xl font-bold text-white mb-6">Submission Portal</h2>
            <div className="mb-6">
              <div className="sidebar-title text-purple-300 text-xs uppercase tracking-wider px-4 mb-2">Account</div>
              <Link to="#" className="flex items-center px-4 py-3 text-white bg-white/10 border-l-4 border-yellow-500">
                <UserPlus className="w-4 h-4 mr-2" /> Sign Up
              </Link>
              <Link to="/portal/login" className="flex items-center px-4 py-3 text-white hover:bg-white/10 transition-all">
                <LogIn className="w-4 h-4 mr-2" /> Sign In
              </Link>
            </div>
          </div>
        </div>

        <div className="flex-1 p-4 md:p-8">
          <div className="max-w-3xl mx-auto">
            {/* Progress Bar Component */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                {[1, 2, 3].map((step) => (
                  <React.Fragment key={step}>
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step < currentStep ? 'bg-green-500 text-white' : step === currentStep ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                        {step < currentStep ? <CheckCircle className="w-5 h-5" /> : step}
                      </div>
                      <span className="text-xs mt-2 text-gray-600">{step === 1 ? 'Personal' : step === 2 ? 'Professional' : 'Account'}</span>
                    </div>
                    {step < 3 && <div className={`flex-1 h-1 mx-2 ${step < currentStep ? 'bg-green-500' : 'bg-gray-200'}`} />}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="p-4 md:p-8">
                <h3 className="text-2xl font-bold text-center text-gray-800 mb-6">Create Your Account</h3>
                {message.text && (
                  <div className={`mb-6 p-4 rounded-lg flex items-center ${message.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                    {message.type === 'error' ? <AlertCircle className="w-5 h-5 mr-2" /> : <CheckCircle className="w-5 h-5 mr-2" />}
                    {message.text}
                  </div>
                )}
                
                <form onSubmit={handleSubmit}>
                   {/* Step 1: Personal Information */}
                  {currentStep === 1 && (
                    <div className="space-y-6">
                      {/* Prefix and Name Row */}
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6">
                        <div className="md:col-span-1">
                          <label htmlFor="prefix" className="block text-sm font-medium text-gray-700 mb-1">
                            Prefix <span className="text-red-500">*</span>
                          </label>
                          <select
                            id="prefix"
                            name="prefix"
                            value={formData.prefix}
                            onChange={handleChange}
                            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                              errors.prefix ? 'border-red-500' : 'border-gray-300'
                            }`}
                          >
                            <option value="">Select Prefix</option>
                            <option value="Prof.">Prof.</option>
                            <option value="Dr.">Dr.</option>
                            <option value="Mr.">Mr.</option>
                            <option value="Mrs.">Mrs.</option>
                            <option value="Miss">Miss</option>
                          </select>
                          {errors.prefix && (
                            <p className="mt-1 text-xs text-red-500">{errors.prefix}</p>
                          )}
                        </div>

                        <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                          <div>
                            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                              First Name <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <input
                                type="text"
                                id="firstName"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                className={`w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                                  errors.firstName ? 'border-red-500' : 'border-gray-300'
                                }`}
                                placeholder="First Name"
                              />
                              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            </div>
                            {errors.firstName && (
                              <p className="mt-1 text-xs text-red-500">{errors.firstName}</p>
                            )}
                          </div>

                          <div>
                            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                              Last Name <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <input
                                type="text"
                                id="lastName"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                className={`w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                                  errors.lastName ? 'border-red-500' : 'border-gray-300'
                                }`}
                                placeholder="Last Name"
                              />
                              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            </div>
                            {errors.lastName && (
                              <p className="mt-1 text-xs text-red-500">{errors.lastName}</p>
                            )}
                          </div>

                          <div>
                            <label htmlFor="otherName" className="block text-sm font-medium text-gray-700 mb-1">
                              Other Name
                            </label>
                            <input
                              type="text"
                              id="otherName"
                              name="otherName"
                              value={formData.otherName}
                              onChange={handleChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                              placeholder="Other Name (Optional)"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Email and ORCID */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                            Email Address <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <input
                              type="email"
                              id="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              className={`w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                                errors.email ? 'border-red-500' : 'border-gray-300'
                              }`}
                              placeholder="Email Address"
                            />
                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                          </div>
                          {errors.email && (
                            <p className="mt-1 text-xs text-red-500">{errors.email}</p>
                          )}
                        </div>

                        <div>
                          <label htmlFor="orcid" className="block text-sm font-medium text-gray-700 mb-1">
                            ORCID ID (Optional)
                          </label>
                          <input
                            type="text"
                            id="orcid"
                            name="orcid"
                            value={formData.orcid}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                            placeholder="ORCID ID"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 2: Professional Information */}
                  {currentStep === 2 && (
                    <div className="space-y-6">
                      {/* Affiliation Details */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                        <div>
                          <label htmlFor="affiliation" className="block text-sm font-medium text-gray-700 mb-1">
                            Affiliation <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              id="affiliation"
                              name="affiliation"
                              value={formData.affiliation}
                              onChange={handleChange}
                              className={`w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                                errors.affiliation ? 'border-red-500' : 'border-gray-300'
                              }`}
                              placeholder="Institution/Organization"
                            />
                            <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                          </div>
                          {errors.affiliation && (
                            <p className="mt-1 text-xs text-red-500">{errors.affiliation}</p>
                          )}
                        </div>

                        <div>
                          <label htmlFor="affiliationCountry" className="block text-sm font-medium text-gray-700 mb-1">
                            Country <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              id="affiliationCountry"
                              name="affiliationCountry"
                              value={formData.affiliationCountry}
                              onChange={handleChange}
                              className={`w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                                errors.affiliationCountry ? 'border-red-500' : 'border-gray-300'
                              }`}
                              placeholder="Country"
                            />
                            <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                          </div>
                          {errors.affiliationCountry && (
                            <p className="mt-1 text-xs text-red-500">{errors.affiliationCountry}</p>
                          )}
                        </div>

                        <div>
                          <label htmlFor="affiliationCity" className="block text-sm font-medium text-gray-700 mb-1">
                            City <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              id="affiliationCity"
                              name="affiliationCity"
                              value={formData.affiliationCity}
                              onChange={handleChange}
                              className={`w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                                errors.affiliationCity ? 'border-red-500' : 'border-gray-300'
                              }`}
                              placeholder="City"
                            />
                            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                          </div>
                          {errors.affiliationCity && (
                            <p className="mt-1 text-xs text-red-500">{errors.affiliationCity}</p>
                          )}
                        </div>
                      </div>

                      {/* Discipline */}
                      <div>
                        <label htmlFor="discipline" className="block text-sm font-medium text-gray-700 mb-1">
                          Primary Discipline <span className="text-red-500">*</span>
                        </label>
                        <select
                          id="discipline"
                          name="discipline"
                          value={formData.discipline}
                          onChange={handleChange}
                          className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                            errors.discipline ? 'border-red-500' : 'border-gray-300'
                          }`}
                        >
                          <option value="">Select a Discipline</option>
                          {disciplineOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                        {errors.discipline && (
                          <p className="mt-1 text-xs text-red-500">{errors.discipline}</p>
                        )}
                      </div>

                      {/* Other Discipline Input */}
                      {showOtherDiscipline && (
                        <div>
                          <label htmlFor="otherDiscipline" className="block text-sm font-medium text-gray-700 mb-1">
                            Please Specify Your Discipline <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            id="otherDiscipline"
                            name="otherDiscipline"
                            value={formData.otherDiscipline}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                            placeholder="Enter your discipline"
                          />
                        </div>
                      )}

                      {/* Review Availability - Styled as Blocks */}
                      <div>
                        <p className="block text-sm font-medium text-gray-700 mb-3">
                          Are you available to review for ASFIRJ? (optional)
                        </p>
                        <div className="grid grid-cols-2 gap-4">
                          <label className={`relative flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            formData.reviewAvailability === 'yes' 
                              ? 'border-purple-600 bg-purple-50' 
                              : 'border-gray-200 hover:border-purple-300 hover:bg-[#f6ecf6]'
                          }`}>
                            <input
                              type="radio"
                              name="reviewAvailability"
                              value="yes"
                              checked={formData.reviewAvailability === 'yes'}
                              onChange={handleRadioChange}
                              className="sr-only"
                            />
                            <div className="text-center">
                              <div className={`text-lg font-semibold mb-1 ${
                                formData.reviewAvailability === 'yes' ? 'text-purple-600' : 'text-gray-700'
                              }`}>
                                Yes
                              </div>
                              <div className={`text-sm ${
                                formData.reviewAvailability === 'yes' ? 'text-purple-600' : 'text-gray-500'
                              }`}>
                                I'm available to review
                              </div>
                              {formData.reviewAvailability === 'yes' && (
                                <div className="absolute top-2 right-2 text-purple-600">
                                  <CheckCircle className="w-5 h-5" />
                                </div>
                              )}
                            </div>
                          </label>

                          <label className={`relative flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            formData.reviewAvailability === 'no' 
                              ? 'border-purple-600 bg-purple-50' 
                              : 'border-gray-200 hover:border-purple-300 hover:bg-[#f6ecf6]'
                          }`}>
                            <input
                              type="radio"
                              name="reviewAvailability"
                              value="no"
                              checked={formData.reviewAvailability === 'no'}
                              onChange={handleRadioChange}
                              className="sr-only"
                            />
                            <div className="text-center">
                              <div className={`text-lg font-semibold mb-1 ${
                                formData.reviewAvailability === 'no' ? 'text-purple-600' : 'text-gray-700'
                              }`}>
                                No
                              </div>
                              <div className={`text-sm ${
                                formData.reviewAvailability === 'no' ? 'text-purple-600' : 'text-gray-500'
                              }`}>
                                Not available to review
                              </div>
                              {formData.reviewAvailability === 'no' && (
                                <div className="absolute top-2 right-2 text-purple-600">
                                  <CheckCircle className="w-5 h-5" />
                                </div>
                              )}
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}


                  {currentStep === 3 && (
                    <div className="space-y-6 animate-in fade-in duration-500">
                     {/* Password Fields */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                        <div className="relative">
                          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                            Create Password <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <input
                              type={showPassword ? 'text' : 'password'}
                              id="password"
                              name="password"
                              value={formData.password}
                              onChange={handleChange}
                              className={`w-full pl-10 pr-10 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                                errors.password ? 'border-red-500' : 'border-gray-300'
                              }`}
                              placeholder="Password (min. 8 characters)"
                            />
                            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            >
                              {showPassword ? (
                                <EyeOff className="w-4 h-4" />
                              ) : (
                                <Eye className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                          {errors.password && (
                            <p className="mt-1 text-xs text-red-500">{errors.password}</p>
                          )}
                        </div>

                        <div className="relative">
                          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                            Confirm Password <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <input
                              type={showConfirmPassword ? 'text' : 'password'}
                              id="confirmPassword"
                              name="confirmPassword"
                              value={formData.confirmPassword}
                              onChange={handleChange}
                              className={`w-full pl-10 pr-10 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                                errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                              }`}
                              placeholder="Confirm Password"
                            />
                            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <button
                              type="button"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            >
                              {showConfirmPassword ? (
                                <EyeOff className="w-4 h-4" />
                              ) : (
                                <Eye className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                          {errors.confirmPassword && (
                            <p className="mt-1 text-xs text-red-500">{errors.confirmPassword}</p>
                          )}
                        </div>
                      </div>

                        {/* Password Requirements */}
                      <div className="text-xs text-gray-500 bg-[#f6ecf6] p-4 rounded-lg">
                        <p className="font-medium mb-2">Password must contain:</p>
                        <ul className="list-disc pl-5 space-y-1">
                          <li>At least 8 characters</li>
                          <li>At least one uppercase letter</li>
                          <li>At least one lowercase letter</li>
                          <li>At least one number</li>
                        </ul>
                      </div>


                      {/* reCAPTCHA Widget - Container must exist when currentStep is 3 */}
                      <div className="mt-6 flex flex-col items-center">
                        <div id="recaptcha-container"></div>
                        {!recaptchaVerified && (
                           <p className="text-xs text-gray-500 mt-2">Verification required to create account</p>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between mt-8">
                    {currentStep > 1 && (
                      <button type="button" onClick={handlePreviousStep} className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md flex items-center gap-2">
                        <ChevronLeft className="w-4 h-4" /> Previous
                      </button>
                    )}
                    
                    {currentStep < 3 ? (
                      <button type="button" onClick={handleNextStep} className="px-6 py-2 bg-purple-600 text-white rounded-md ml-auto flex items-center gap-2">
                        Next <ChevronRight className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        type="submit"
                        disabled={(!recaptchaVerified && process.env.NODE_ENV !== 'development') || isSubmitting}
                        className={`px-6 py-2 rounded-md text-white ml-auto flex items-center gap-2 ${(!recaptchaVerified && process.env.NODE_ENV !== 'development') || isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
                      >
                        {isSubmitting ? <Loader className="animate-spin h-5 w-5" /> : <CheckCircle className="w-5 h-5" />}
                        {isSubmitting ? 'Creating Account...' : 'Create Account'}
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthorSignup;